// PowerComposer — the send-key convention, pure.
//
// Enter sends, Shift+Enter inserts a newline: this is now universal enough
// to be load-bearing (Jakob's law) across ChatGPT, Claude, and Cursor
// (.claude/analysis/chat-composer-research.md §2) — "deviating here is not a
// design opportunity, it is a Jakob's-law tax on every returning user."
// PowerComposer keeps that as the DEFAULT and does not invent a fourth
// binding, but — being the power-user direction — exposes the one
// alternative binding real tools also offer (Slack/Cursor-style remap):
// mod-Enter (Cmd on mac, Ctrl elsewhere) sends, plain Enter always inserts a
// newline. A caller opts into this via `sendKey="modEnter"`; it is never
// the shipped default.
//
// An IME composition's Enter (committing the composed character, e.g. in
// Japanese/Korean/Chinese input) must never send, in EITHER binding — that
// check lives here too so it can't be forgotten at a call site.

export type SendKey = "enter" | "modEnter";

// A minimal, DOM-independent mirror of the KeyboardEvent fields the send
// decision needs — kept as its own type so the decision is testable without
// constructing a real KeyboardEvent.
export type KeyChord = {
    readonly key: string;
    readonly shiftKey: boolean;
    readonly metaKey: boolean;
    readonly ctrlKey: boolean;
    readonly composing: boolean;
};

export const isSendChord = (chord: KeyChord, sendKey: SendKey): boolean => {
    if (chord.composing) return false;
    if (chord.key !== "Enter") return false;
    if (sendKey === "enter") return !chord.shiftKey;
    return chord.metaKey || chord.ctrlKey;
};
